import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Username } from "./UserBadge";
import { Settings, Menu, Megaphone, BarChart2, Folder, Bold, Link as LinkIcon, Image as ImageIcon, Smile, FileVideo, Quote, Type, Paperclip, Users, Pencil } from "lucide-react";
import { formatRelativeTime } from "../lib/utils";

export default function ChatboxWidget({ currentBoardId = "all" }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeRoomId, setActiveRoomId] = useState(null);
  
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [inputText, setInputText] = useState("");
  const [showOnlineUsers, setShowOnlineUsers] = useState(false);
  const [showRoomsDropdown, setShowRoomsDropdown] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [activeMessageActionsId, setActiveMessageActionsId] = useState(null);
  
  // New States
  const [isEditingAnnouncement, setIsEditingAnnouncement] = useState(false);
  const [announcementInput, setAnnouncementInput] = useState("");
  const [chatRoles, setChatRoles] = useState([]);
  const [showMentionsList, setShowMentionsList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  
  // Staff Chat States
  const [showStaffMembersPopover, setShowStaffMembersPopover] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffSearchInput, setStaffSearchInput] = useState("");
  const [showStaffSearchDropdown, setShowStaffSearchDropdown] = useState(false);
  const [staffSearchResults, setStaffSearchResults] = useState([]);
  
  // Moderation Popup and Appeal States
  const [isModActionModalOpen, setIsModActionModalOpen] = useState(false);
  const [modActionData, setModActionData] = useState({ actionType: "", targetUsername: "", roomId: "" });
  const [modReason, setModReason] = useState("");
  const [modDuration, setModDuration] = useState(15);
  const [modProofUrl, setModProofUrl] = useState("");
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [uploadProofSuccess, setUploadProofSuccess] = useState(false);

  const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
  const [appealData, setAppealData] = useState({ actionType: "", reason: "", moderatorUsername: "", proofUrl: "" });
  const [appealText, setAppealText] = useState("");
  const [isSubmittingAppeal, setIsSubmittingAppeal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [searchedUsers, setSearchedUsers] = useState([]);

  useEffect(() => {
    if (!mentionQuery.trim()) {
      setSearchedUsers([]);
      return;
    }
    
    const delayDebounce = setTimeout(() => {
      api.get(`/users?q=${encodeURIComponent(mentionQuery)}&limit=10`)
        .then((res) => {
          setSearchedUsers(res.data || []);
        })
        .catch((err) => {
          console.error("Error fetching users for autocomplete:", err);
        });
    }, 150);
    
    return () => clearTimeout(delayDebounce);
  }, [mentionQuery]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("appeal") === "1") {
      const actionType = params.get("action") || "";
      const reason = params.get("reason") || "";
      const moderatorUsername = params.get("mod") || "";
      const proofUrl = params.get("proof") || "";
      
      setAppealData({
        actionType,
        reason,
        moderatorUsername,
        proofUrl
      });
      setIsAppealModalOpen(true);
    }
  }, []);

  const handleCloseAppeal = () => {
    setIsAppealModalOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("appeal");
    url.searchParams.delete("action");
    url.searchParams.delete("reason");
    url.searchParams.delete("mod");
    url.searchParams.delete("proof");
    window.history.replaceState({}, document.title, url.pathname + url.search);
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSizeBytes = 20 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      alert("Fișierul este prea mare! Limita este de 20MB.");
      return;
    }
    
    setIsUploadingProof(true);
    setUploadProofSuccess(false);
    
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", "proof");
    
    try {
      const res = await api.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      setModProofUrl(res.data.url);
      setUploadProofSuccess(true);
    } catch (err) {
      console.error(err);
      alert("Încărcarea fișierului a eșuat. Vă rugăm să reîncercați.");
    } finally {
      setIsUploadingProof(false);
    }
  };

  const handleModActionSubmit = async (e) => {
    e.preventDefault();
    if (!modActionData.targetUsername || !modReason) {
      alert("Vă rugăm să completați toate câmpurile obligatorii.");
      return;
    }
    
    try {
      const res = await api.post("/idg/chat/moderation/action", {
        room_id: modActionData.roomId,
        target_username: modActionData.targetUsername,
        action_type: modActionData.actionType,
        duration: modActionData.actionType === "kick" ? null : parseInt(modDuration),
        reason: modReason,
        proof_url: modProofUrl || null
      });
      
      if (res.data && res.data.message) {
        setMessages((prev) => [...prev, res.data.message]);
      }
      
      setIsModActionModalOpen(false);
      setModReason("");
      setModDuration(15);
      setModProofUrl("");
      setUploadProofSuccess(false);
      setInputText(""); // clear the input text box since command completed
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Acțiunea a eșuat.");
    }
  };

  const handleAppealSubmit = async (e) => {
    e.preventDefault();
    if (!appealText.trim()) {
      alert("Vă rugăm să scrieți o justificare.");
      return;
    }
    
    setIsSubmittingAppeal(true);
    try {
      await api.post("/idg/chat/moderation/appeal", {
        action_type: appealData.actionType,
        reason: appealData.reason,
        moderator_username: appealData.moderatorUsername,
        proof_url: appealData.proofUrl || null,
        appeal_text: appealText
      });
      
      alert("Contestația a fost trimisă cu succes către toți administratorii!");
      handleCloseAppeal();
      setAppealText("");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Trimiterea contestației a eșuat.");
    } finally {
      setIsSubmittingAppeal(false);
    }
  };

  const messagesContainerRef = useRef(null);

  // Load rooms, settings and roles
  useEffect(() => {
    api.get(`/idg/chat/rooms?board_id=${currentBoardId}`).then((r) => {
      setRooms(r.data.rooms || []);
      setSettings(r.data.settings || {});
      if (r.data.rooms && r.data.rooms.length > 0) {
        setActiveRoomId(r.data.rooms[0].id);
      }
    }).catch(() => {
      // Local Mock for rooms if API fails
      setRooms([{ id: "mock-1", name: "Lobby", description: "Main Local Chat" }]);
      setActiveRoomId("mock-1");
    });
    
    api.get("/idg/chat/roles").then((r) => {
      setChatRoles(r.data || []);
    }).catch(() => {
      setChatRoles([]);
    });
  }, [currentBoardId]);

  // Poll for messages and online status
  useEffect(() => {
    if (!activeRoomId) return;
    
    const fetchState = () => {
      api.get(`/idg/chat/rooms/${activeRoomId}/state`).then((r) => {
        setMessages(prev => {
          if (JSON.stringify(prev) === JSON.stringify(r.data.messages)) {
             return prev;
          }
          return r.data.messages;
        });
        setOnlineUsers(prev => {
          if (prev.length === r.data.online_users.length) return prev;
          return r.data.online_users;
        });
      }).catch(() => {
        // Local Mock for state
        setOnlineUsers([{ id: "me", username: user?.username || "You", role: "admin", is_staff: true }]);
      });
    };
    
    fetchState();
    const interval = setInterval(fetchState, 3000);
    return () => clearInterval(interval);
  }, [activeRoomId]);

  // Auto-scroll to bottom when messages change
  const prevMessagesLength = useRef(0);
  useEffect(() => {
    if (messagesContainerRef.current) {
        if (messages.length > prevMessagesLength.current) {
            setTimeout(() => {
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                }
            }, 10);
        }
        prevMessagesLength.current = messages.length;
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !activeRoomId || !user) return;
    
    const content = inputText.trim();
    
    // Intercept moderation commands for popup modal
    const parts = content.split(" ");
    const cmd = parts[0].toLowerCase();
    if (["/mute", "/ban", "/kick"].includes(cmd)) {
      const isStaff = user && ["admin", "root", "mod"].includes(user.role);
      if (isStaff) {
        const targetUsername = parts[1] ? parts[1].replace(/^@/, "") : "";
        setModActionData({
          actionType: cmd.substring(1), // "mute", "ban", "kick"
          targetUsername: targetUsername,
          roomId: activeRoomId
        });
        setIsModActionModalOpen(true);
        return;
      }
    }
    
    setInputText(""); // optimistic clear
    
    if (content.toLowerCase() === "/comenzi") {
      const helpMsg = {
        id: "help_" + Date.now(),
        room_id: activeRoomId,
        user_id: "system",
        username: "System",
        avatar_url: "https://api.dicebear.com/7.x/bottts/svg?seed=System",
        role: "system",
        content: `Comenzi Chatbox valabile:
• /comenzi - Afișează această listă de comenzi.
• /img <link_imagine> - Adaugă o imagine în chat.
• /yt <link_youtube> - Adaugă un video YouTube în chat.
• /tk <link_tiktok> - Adaugă un video TikTok în chat.
• /ig <link_instagram> - Adaugă o postare/reel Instagram în chat.
• /clear - Șterge toate mesajele din cameră (doar Staff).
• /kick <username> - Deconectează un utilizator (doar Staff).
• /mute <username> [minute] - Pune mute unui utilizator (doar Staff).
• /ban <username> - Pune ban unui utilizator pe chat (doar Staff).`,
        created_at: new Date().toISOString()
      };
      setMessages((prev) => [...prev, helpMsg]);
      return;
    }
    
    try {
      const res = await api.post(`/idg/chat/rooms/${activeRoomId}/messages`, { content });
      setMessages((prev) => [...prev, res.data]);
    } catch (err) {
      console.error(err);
      // Local Mock for sending messages if API fails
      const mockMsg = {
        id: "mock_msg_" + Date.now(),
        room_id: activeRoomId,
        user_id: user?.id || "local",
        username: user?.username || "Local User",
        avatar_url: user?.avatar_url || `https://api.dicebear.com/7.x/bottts/svg?seed=${user?.username || "Local"}`,
        role: user?.role || "user",
        content: content,
        created_at: new Date().toISOString(),
        reactions: {}
      };
      setMessages((prev) => [...prev, mockMsg]);
    }
  };

  const toggleReaction = async (msgId, emoji) => {
    if (!user) return;
    try {
      const res = await api.post(`/idg/chat/rooms/${activeRoomId}/messages/${msgId}/react`, { emoji });
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, reactions: res.data.reactions } : msg))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const saveEdit = async (msgId) => {
    if (!editingContent.trim()) return;
    try {
      await api.patch(`/idg/chat/rooms/${activeRoomId}/messages/${msgId}`, { content: editingContent });
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, content: editingContent, edited_at: new Date().toISOString() } : msg))
      );
      setEditingMessageId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInputChange = (val) => {
    setInputText(val);
    const lastWord = val.split(" ").pop() || "";
    if (lastWord.startsWith("@") && lastWord.length > 1) {
      setShowMentionsList(true);
      setMentionQuery(lastWord.substring(1).toLowerCase());
    } else {
      setShowMentionsList(false);
    }
  };

  const handleSelectMention = (roleSlug) => {
    const words = inputText.split(" ");
    words.pop();
    words.push(`@${roleSlug}`);
    setInputText(words.join(" ") + " ");
    setShowMentionsList(false);
  };

  const handleBoldClick = () => {
    setInputText((prev) => prev + "**text**");
  };

  const handleLinkClick = () => {
    const url = window.prompt("Introduceți URL-ul link-ului:");
    if (url) setInputText((prev) => prev + ` ${url}`);
  };

  const handleImageClick = () => {
    const url = window.prompt("Introduceți URL-ul imaginii:");
    if (url) setInputText((prev) => prev + ` /img ${url}`);
  };

  const saveAnnouncement = async () => {
    try {
      await api.post("/idg/chat/settings/announcement", { announcement_text: announcementInput });
      setSettings((s) => ({ ...s, announcement_text: announcementInput }));
      setIsEditingAnnouncement(false);
    } catch (err) {
      console.error(err);
    }
  };

  const loadStaffRoomMembers = async () => {
    if (!activeRoomId) return;
    try {
      const res = await api.get(`/idg/chat/rooms/${activeRoomId}/members`);
      setStaffMembers(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStaffSearchChange = async (val) => {
    setStaffSearchInput(val);
    if (val.trim().length > 1) {
      try {
        const res = await api.get(`/members?q=${val}`);
        setStaffSearchResults(res.data || []);
        setShowStaffSearchDropdown(true);
      } catch (err) {
        console.error(err);
      }
    } else {
      setShowStaffSearchDropdown(false);
    }
  };

  const addStaffRoomMember = async (targetUser) => {
    try {
      await api.post(`/idg/chat/rooms/${activeRoomId}/members`, { user_id: targetUser.id });
      setStaffSearchInput("");
      setShowStaffSearchDropdown(false);
      loadStaffRoomMembers();
    } catch (err) {
      console.error(err);
    }
  };

  const removeStaffRoomMember = async (targetUserId) => {
    try {
      await api.delete(`/idg/chat/rooms/${activeRoomId}/members/${targetUserId}`);
      loadStaffRoomMembers();
    } catch (err) {
      console.error(err);
    }
  };

  const activeRoom = rooms.find((r) => r.id === activeRoomId);

  const activeRoomName = rooms.find((r) => r.id === activeRoomId)?.name || "Lobby";
  const unreadCount = 0; // Stub, could be calculated

  const getYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getTikTokId = (url) => {
    const regExp = /\/video\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const getInstagramShortcode = (url) => {
    const match = url.match(/\/p\/([a-zA-Z0-9_-]+)/) || url.match(/\/reel\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  const formatText = (text) => {
    if (!text) return text;
    // Parse Markdown bold **text**, [b]text[/b], mentions @name or @bot/@ai, links
    const parts = text.split(/(\*\*.*?\*\*|\[b\].*?\[\/b\]|@\w+|https?:\/\/[^\s]+)/g);
    
    return parts.map((part, i) => {
      if (!part) return null;
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("[b]") && part.endsWith("[/b]")) {
        return <strong key={i} className="font-bold text-white">{part.slice(3, -4)}</strong>;
      }
      if (part.startsWith("@")) {
        const isBotMention = part.toLowerCase() === "@bot" || part.toLowerCase() === "@ai";
        return (
          <span 
            key={i} 
            className={`inline-flex items-center gap-1 font-bold px-1.5 py-0.5 rounded text-xs mx-0.5 font-mono ${
              isBotMention 
                ? "bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40 shadow-[0_0_8px_rgba(255,79,0,0.25)]" 
                : "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
            }`}
          >
            {part}
          </span>
        );
      }
      if (part.match(/^https?:\/\/[^\s]+/)) {
        return (
          <a key={i} href={part} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline inline-flex items-center gap-1 break-all">
            {part}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  if (rooms.length === 0) return null; // App is disabled or user has no view permission

  const canPost = () => {
    if (!user) return false;
    const allowed = settings.allowed_roles_post || ["user", "vip", "mod", "admin", "root"];
    if (user.role === "admin" || user.role === "root") return true;
    return allowed.includes(user.role);
  };

  const widgetStyle = {
    width: settings?.widget_width || undefined,
    padding: (settings?.widget_padding && /^[0-9]+(px|rem|em|%)?$/i.test(settings.widget_padding.trim())) ? settings.widget_padding : undefined,
    fontFamily: settings?.chat_font_family ? (settings.chat_font_family === "monospace" ? "monospace" : `${settings.chat_font_family}, sans-serif`) : undefined,
  };

  const containerHeight = (settings?.widget_height && /^[0-9]+(px|rem|em|vh)?$/i.test(settings.widget_height.trim())) ? settings.widget_height : "230px";
  const innerPadding = settings?.chat_inner_padding || "normal";
  
  let listPaddingClass = "py-1.5 px-2.5 space-y-0.5";
  let avatarSize = 22;
  let textClass = "text-xs sm:text-sm leading-tight";
  let itemGapClass = "gap-2";
  let timeClass = "text-[10px]";
  
  if (innerPadding === "compact") {
    listPaddingClass = "py-1 px-2 space-y-0";
    avatarSize = 18;
    textClass = "text-xs leading-none";
    itemGapClass = "gap-1.5";
    timeClass = "text-[9px]";
  } else if (innerPadding === "cozy") {
    listPaddingClass = "py-2 px-3 space-y-1";
    avatarSize = 24;
    textClass = "text-sm leading-snug";
    itemGapClass = "gap-2.5";
    timeClass = "text-[11px]";
  }

  const EMOJI_LIST = ["👍", "❤️", "😂", "🔥", "💀", "👑", "🎯", "🚀", "🎮", "⚡", "🏆", "😎", "👏", "🎉", "💯", "✨", "🇷🇴", "🤝"];

  return (
    <div className="w-full mb-6" style={widgetStyle} data-testid="chatbox-widget">
      <div className="flex flex-col border border-white/10 overflow-hidden bg-[#07090e] shadow-2xl">
        
        {/* Header Bar */}
        <div className="flex justify-between items-center px-4 py-3 bg-[#0c0f17] border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 bg-[var(--tf-primary)]/15 border border-[var(--tf-primary)]/30 flex items-center justify-center">
              <MessageIcon className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
            </div>
            <h3 className="text-xs font-black tracking-wider text-white uppercase font-chakra">
              Chatbox Comunitate
            </h3>
          </div>

          <div className="flex items-center gap-3 text-zinc-400">
            <button
              onClick={() => setShowOnlineUsers(!showOnlineUsers)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono font-bold transition-all border ${
                showOnlineUsers 
                  ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)] font-black" 
                  : "bg-white/5 border-white/10 text-zinc-300 hover:text-white hover:bg-white/10"
              }`}
              title="Membri Online"
            >
              <Users className="w-3.5 h-3.5" />
              <span>{onlineUsers.length}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            </button>
            <Settings className="w-4 h-4 cursor-pointer hover:text-white transition-colors" title="Setări Chat" />
            <Menu className="w-4 h-4 cursor-pointer hover:text-white transition-colors" />
          </div>
        </div>

        {/* Announcement Banner */}
        {(settings.announcement_text || (user && (user.role === "admin" || user.role === "root"))) && (
          <div className="flex justify-between items-center px-4 py-2 bg-gradient-to-r from-orange-500/10 via-[#0a0c12] to-[#07090e] border-b border-white/10 text-xs text-zinc-300">
            <div className="flex items-center gap-2 flex-1 min-w-0 font-mono text-[11px]">
              <Megaphone className="w-3.5 h-3.5 text-[var(--tf-primary)] flex-shrink-0" />
              {isEditingAnnouncement ? (
                <input
                  type="text"
                  value={announcementInput}
                  onChange={(e) => setAnnouncementInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveAnnouncement();
                    if (e.key === "Escape") setIsEditingAnnouncement(false);
                  }}
                  className="flex-1 bg-black/80 border border-white/15 px-2 py-0.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                  placeholder="Scrie anunț..."
                  autoFocus
                />
              ) : (
                <span className="truncate font-medium text-zinc-200">
                  {settings.announcement_text || <span className="italic text-zinc-500">Fără anunț activ. Apasă pe creion pentru a adăuga unul.</span>}
                </span>
              )}
            </div>
            {user && (user.role === "admin" || user.role === "root") && (
              <div className="ml-2 flex-shrink-0">
                {isEditingAnnouncement ? (
                  <div className="flex gap-2">
                    <button onClick={saveAnnouncement} className="text-[10px] text-[var(--tf-primary)] font-bold hover:underline">Salvez</button>
                    <button onClick={() => setIsEditingAnnouncement(false)} className="text-[10px] text-zinc-500 hover:underline">Renunț</button>
                  </div>
                ) : (
                  <button onClick={() => { setIsEditingAnnouncement(true); setAnnouncementInput(settings.announcement_text || ""); }} className="text-zinc-500 hover:text-white" title="Editează anunț">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Sub-header Tabs */}
        <div className="relative z-20 flex items-center px-4 py-2 border-b border-white/10 text-xs font-bold uppercase tracking-wider bg-[#090c12] font-chakra">
          <div 
            className="flex items-center gap-1.5 text-white cursor-pointer mr-6 hover:text-[var(--tf-primary)] transition-colors"
            onClick={() => setShowRoomsDropdown(!showRoomsDropdown)}
          >
            <span className="text-[var(--tf-primary)]">●</span>
            <span>{activeRoomName}</span>
            {unreadCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-[var(--tf-primary)] text-black flex items-center justify-center text-[9px]">{unreadCount}</span>
            )}
          </div>
          
          {settings.polls_enabled && (
            <div className="flex items-center gap-1.5 text-zinc-400 hover:text-white cursor-pointer transition-colors mr-6">
              <BarChart2 className="w-3.5 h-3.5" />
              <span>Sondaje</span>
            </div>
          )}

          <div className="flex items-center gap-4">
            <div 
              className="flex items-center gap-1.5 text-zinc-400 hover:text-white cursor-pointer transition-colors"
              onClick={() => setShowRoomsDropdown(!showRoomsDropdown)}
            >
              <Folder className="w-3.5 h-3.5" />
              <span>Camere ({rooms.length})</span>
            </div>

            {/* Staff members toggle if room is_staff and user is mod/admin */}
            {activeRoom?.is_staff && user && (user.role === "admin" || user.role === "root" || user.role === "mod") && (
              <div className="relative">
                <button 
                  onClick={() => { setShowStaffMembersPopover(!showStaffMembersPopover); loadStaffRoomMembers(); }}
                  className="flex items-center gap-1.5 text-zinc-400 hover:text-white cursor-pointer transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>Membri Cameră Privată</span>
                </button>

                {/* Staff members management popover */}
                {showStaffMembersPopover && (
                  <div className="absolute top-8 left-0 w-72 border border-white/10 bg-[#0c0f17] shadow-2xl z-30 p-3 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold border-b border-white/10 pb-1.5 mb-2 flex justify-between items-center font-mono">
                      <span>Membri Cameră Privată</span>
                      <button onClick={() => setShowStaffMembersPopover(false)} className="text-zinc-500 hover:text-white font-bold">X</button>
                    </div>
                    
                    <div className="max-h-36 overflow-y-auto space-y-1.5 mb-3 custom-scrollbar">
                      {staffMembers.map((m) => (
                        <div key={m.id} className="flex justify-between items-center text-xs p-1 hover:bg-white/5">
                          <span className="text-zinc-300 font-medium">{m.username}</span>
                          <button onClick={() => removeStaffRoomMember(m.id)} className="text-red-500 hover:text-red-400 text-[10px]">Șterge</button>
                        </div>
                      ))}
                      {staffMembers.length === 0 && <p className="text-[10px] text-zinc-500 italic">Niciun membru extern adăugat.</p>}
                    </div>

                    <div className="relative space-y-1.5">
                      <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider block">Adaugă Membru</span>
                      <input
                        type="text"
                        value={staffSearchInput}
                        onChange={(e) => handleStaffSearchChange(e.target.value)}
                        className="w-full bg-black/60 border border-white/15 p-1.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                        placeholder="Caută membru (minim 2 caractere)..."
                      />
                      
                      {showStaffSearchDropdown && (
                        <div className="absolute bottom-8 left-0 right-0 max-h-36 overflow-y-auto border border-white/15 bg-black/95 shadow-xl z-40 py-1">
                          {staffSearchResults.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => addStaffRoomMember(u)}
                              className="w-full text-left px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/5 transition-colors flex items-center justify-between"
                            >
                              <span>{u.username}</span>
                              <span className="text-[9px] text-zinc-500 uppercase">{u.role}</span>
                            </button>
                          ))}
                          {staffSearchResults.length === 0 && (
                            <div className="text-[10px] text-zinc-500 px-3 py-1.5 italic">Niciun rezultat.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rooms Selection Dropdown */}
          {showRoomsDropdown && (
            <div className="absolute top-9 left-4 w-60 border border-white/15 bg-[#0c0f17] shadow-2xl z-30 py-1.5 animate-in fade-in duration-200">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest px-3 py-1 border-b border-white/10 mb-1.5 font-bold font-mono">
                Schimbă Camera
              </div>
              {rooms.map((r) => (
                <button
                  key={r.id}
                  onClick={() => {
                    setActiveRoomId(r.id);
                    setShowRoomsDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-white/5 transition-colors font-mono ${
                    r.id === activeRoomId ? "text-[var(--tf-primary)] font-bold bg-[var(--tf-primary)]/10 border-l-2 border-[var(--tf-primary)]" : "text-zinc-300"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{r.name}</span>
                    {r.is_staff && <span className="text-[8px] bg-red-900/60 border border-red-700 text-red-300 px-1 uppercase font-bold tracking-widest">Staff</span>}
                  </span>
                  {r.id === activeRoomId && <span className="w-1.5 h-1.5 rounded-full bg-[var(--tf-primary)]" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="relative flex bg-[#07090e]" style={{ height: containerHeight }}>
          
          {/* Messages List */}
          <div ref={messagesContainerRef} className={`flex-1 overflow-y-auto custom-scrollbar flex flex-col ${listPaddingClass}`}>
            {messages.length === 0 && (
              <div className="text-zinc-500 text-xs italic m-auto font-mono">
                Niciun mesaj în această cameră. Fii primul care scrie!
              </div>
            )}
            
            {messages.map((m) => {
              const isEditing = m.id === editingMessageId;
              const showActions = activeMessageActionsId === m.id;
              const isBot = m.username?.toLowerCase().includes("bot") || m.user_id === "bot" || m.user_id === "system";

              return (
                <div 
                  key={m.id} 
                  className={`flex items-center group relative ${itemGapClass} cursor-pointer sm:cursor-default hover:bg-white/[0.02] py-0.5 px-1.5 transition-colors`}
                  onClick={() => {
                    if (window.innerWidth <= 640) {
                      setActiveMessageActionsId(showActions ? null : m.id);
                    }
                  }}
                >
                  <Avatar user={m} size={avatarSize} className="flex-shrink-0" hover={true} />
                  
                  <div className="flex-1 min-w-0">
                    <div className={textClass}>
                      <span className="font-bold text-white mr-1.5 inline-flex items-center gap-1.5">
                        <Username user={m} asText showBadge={true} />
                        {isBot && (
                          <span className="px-1.5 py-0.2 text-[8px] font-black uppercase font-mono bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            BOT
                          </span>
                        )}
                        <span className="text-zinc-500 font-normal">:</span>
                      </span>

                      {isEditing ? (
                        <div className="mt-1 flex items-center gap-2 max-w-xl">
                          <input
                            type="text"
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            className="flex-1 bg-black/60 border border-white/15 px-2 py-1 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(m.id);
                              if (e.key === "Escape") setEditingMessageId(null);
                            }}
                            autoFocus
                          />
                          <button onClick={() => saveEdit(m.id)} className="text-[10px] bg-[var(--tf-primary)] text-black font-bold px-2 py-1 hover:opacity-90">Salvez</button>
                          <button onClick={() => setEditingMessageId(null)} className="text-[10px] bg-white/10 text-white font-bold px-2 py-1 hover:bg-white/20">Renunț</button>
                        </div>
                      ) : (
                        <div className="inline">
                          <span className="text-zinc-200 break-words font-sans">{formatText(m.content)}</span>
                          
                          {/* Media Embed rendering */}
                          {m.media && (
                            <div className="mt-1.5 overflow-hidden">
                              {m.media.type === "yt" && getYoutubeId(m.media.url) && (
                                <iframe
                                  src={`https://www.youtube.com/embed/${getYoutubeId(m.media.url)}`}
                                  className="w-full max-w-md aspect-video border border-white/10 mt-1"
                                  allowFullScreen
                                />
                              )}
                              {m.media.type === "tk" && getTikTokId(m.media.url) && (
                                <iframe
                                  src={`https://www.tiktok.com/embed/v2/${getTikTokId(m.media.url)}`}
                                  className="w-full max-w-xs h-[480px] border border-white/10 mt-1"
                                />
                              )}
                              {m.media.type === "ig" && getInstagramShortcode(m.media.url) && (
                                <iframe
                                  src={`https://www.instagram.com/p/${getInstagramShortcode(m.media.url)}/embed`}
                                  className="w-full max-w-xs h-[400px] border border-white/10 mt-1"
                                />
                              )}
                              {(m.media.type === "img" || m.media.type === "image") && (
                                <img
                                  src={m.media.url}
                                  alt="chat media"
                                  className="max-h-32 max-w-sm object-contain border border-white/10 mt-1 hover:max-h-96 transition-all duration-200 cursor-zoom-in block"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.currentTarget.classList.toggle("max-h-32");
                                    e.currentTarget.classList.toggle("max-h-96");
                                  }}
                                />
                              )}
                              {m.media.type === "video" && (
                                <video
                                  src={m.media.url}
                                  controls
                                  className="max-h-60 max-w-sm border border-white/10 mt-1 block"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Reactions list */}
                    {!isEditing && m.reactions && Object.keys(m.reactions).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {Object.entries(m.reactions).map(([emoji, userIds]) => {
                          const hasReacted = user && userIds.includes(user.id);
                          return (
                            <button
                              key={emoji}
                              onClick={() => toggleReaction(m.id, emoji)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-bold transition-all ${
                                hasReacted
                                  ? "bg-[var(--tf-primary)]/20 border-[var(--tf-primary)]/40 text-[var(--tf-primary)] font-black"
                                  : "bg-white/5 border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
                              }`}
                            >
                              <span>{emoji}</span>
                              <span className="font-mono">{userIds.length}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Message Hover Actions */}
                  {!isEditing && (
                    <div className={`transition-opacity flex items-center gap-1.5 ml-2 bg-[#0c0f17] px-2 py-1 border border-white/10 shadow-xl ${
                      showActions ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    }`}>
                      {/* Reactions Toolbar */}
                      <div className="flex items-center gap-1">
                        {["👍", "❤️", "😂", "🔥", "👑", "🎯"].map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => toggleReaction(m.id, emoji)}
                            className="hover:scale-125 transition-transform duration-100 text-xs px-0.5"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>

                      {/* Edit button */}
                      {user && (m.user_id === user.id || user.role === "admin" || user.role === "root") && (
                        <button
                          onClick={() => {
                            setEditingMessageId(m.id);
                            setEditingContent(m.content);
                          }}
                          className="p-0.5 text-zinc-400 hover:text-white transition-colors border-l border-white/10 pl-1.5 ml-1"
                          title="Editează mesaj"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}

                  <div className={`text-zinc-500 font-mono opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 px-2 py-0.5 border border-white/5 flex items-center gap-1 flex-shrink-0 ${timeClass}`}>
                    {m.edited_at && <span className="text-[8px] opacity-60">(editat)</span>}
                    <span>{formatTimestamp(m.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Online Users List Drawer */}
          {showOnlineUsers && (
            <div className="absolute top-0 right-0 bottom-0 w-full sm:w-64 border-l border-white/10 bg-[#0c0f17] overflow-y-auto custom-scrollbar p-3 z-10 shadow-2xl animate-in slide-in-from-right-8 fade-in">
              <div className="space-y-3">
                <div className="flex justify-between items-center text-[10px] text-zinc-400 uppercase tracking-widest mb-3 font-bold border-b border-white/10 pb-2 font-chakra">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    Utilizatori Online ({onlineUsers.length})
                  </span>
                  <button 
                    onClick={() => setShowOnlineUsers(false)} 
                    className="text-zinc-400 hover:text-white font-bold text-xs"
                  >
                    ×
                  </button>
                </div>
                {onlineUsers.map((ou) => (
                  <div key={ou.user_id} className="flex gap-2.5 items-center group cursor-pointer hover:bg-white/5 p-1.5 transition-colors">
                    <div className="relative">
                      <Avatar user={ou} size={26} hover={true} />
                      <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full border border-black" />
                    </div>
                    <div className="flex flex-col flex-1 truncate">
                      <span className="text-xs font-bold truncate">
                        <Username user={ou} showBadge={true} />
                      </span>
                      {ou.status_text && <span className="text-[9px] text-zinc-500 font-mono uppercase truncate">{ou.status_text}</span>}
                    </div>
                  </div>
                ))}
                {onlineUsers.length === 0 && (
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest text-center mt-4 font-mono">Niciun utilizator conectat</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        {canPost() ? (
          <div className="p-3 bg-[#090c12] border-t border-white/10 relative">
            
            {/* Emoji Picker Popover */}
            {showEmojiPicker && (
              <div className="absolute bottom-16 left-3 bg-[#0c0f17] border border-white/15 p-3 shadow-2xl z-30 w-72 animate-in fade-in zoom-in-95">
                <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2">
                  <span className="text-[10px] font-mono font-bold uppercase text-zinc-400">Emoticoane & Reacții</span>
                  <button onClick={() => setShowEmojiPicker(false)} className="text-zinc-500 hover:text-white text-xs">×</button>
                </div>
                <div className="grid grid-cols-6 gap-2 text-lg">
                  {EMOJI_LIST.map((em) => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => {
                        setInputText((prev) => prev + em);
                        setShowEmojiPicker(false);
                      }}
                      className="p-1 hover:bg-white/10 transition-transform hover:scale-125 flex items-center justify-center"
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Autocomplete Dropdown for mentions */}
            {showMentionsList && (
              <div className="absolute bottom-16 left-4 max-h-56 overflow-y-auto w-64 border border-white/15 bg-[#0c0f17] shadow-2xl z-20 py-1 font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                {(mentionQuery ? searchedUsers : onlineUsers).length > 0 && (
                  <>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest px-3 py-1 border-b border-white/10 mb-1 font-bold">
                      Menționează Membru
                    </div>
                    {(mentionQuery ? searchedUsers : onlineUsers).map((ou) => (
                      <button
                        key={`user-${ou.user_id || ou.id}`}
                        type="button"
                        onClick={() => handleSelectMention(ou.username)}
                        className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <Avatar user={ou} size={18} />
                        <span>{ou.username}</span>
                      </button>
                    ))}
                  </>
                )}

                {/* Roles Section */}
                {chatRoles.filter((r) => r.slug.toLowerCase().includes(mentionQuery) || r.name.toLowerCase().includes(mentionQuery)).length > 0 && (
                  <>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest px-3 py-1 border-b border-white/10 mb-1 mt-1.5 font-bold">
                      Menționează Rol
                    </div>
                    {chatRoles
                      .filter((r) => r.slug.toLowerCase().includes(mentionQuery) || r.name.toLowerCase().includes(mentionQuery))
                      .map((r) => (
                        <button
                          key={`role-${r.slug}`}
                          type="button"
                          onClick={() => handleSelectMention(r.slug)}
                          className="w-full text-left px-3 py-1.5 text-zinc-300 hover:bg-white/5 transition-colors flex items-center gap-2"
                        >
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color || "#ccc" }} />
                          <span>{r.name}</span>
                        </button>
                      ))}
                  </>
                )}

                {(mentionQuery ? searchedUsers : onlineUsers).length === 0 &&
                 chatRoles.filter((r) => r.slug.toLowerCase().includes(mentionQuery) || r.name.toLowerCase().includes(mentionQuery)).length === 0 && (
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest text-center py-2 italic">
                    Niciun rezultat
                  </div>
                )}
              </div>
            )}

            <form onSubmit={sendMessage} className="flex flex-col border border-white/15 bg-black/70 focus-within:border-[var(--tf-primary)] transition-colors">
              <input
                type="text"
                value={inputText}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Scrie un mesaj... (tastează @ pentru mențiuni sau @BOT pentru asistent)"
                className="w-full bg-transparent text-xs sm:text-sm text-white px-3 py-2.5 outline-none font-sans"
              />
              
              <div className="flex items-center justify-between px-3 py-1.5 bg-[#0c0f17] border-t border-white/10">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={handleBoldClick} className="text-zinc-400 hover:text-white transition-colors" title="Bold (**text**)">
                    <Bold className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={handleLinkClick} className="text-zinc-400 hover:text-[var(--tf-primary)] transition-colors" title="Adaugă Link">
                    <LinkIcon className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={handleImageClick} className="text-zinc-400 hover:text-[var(--tf-primary)] transition-colors" title="Adaugă Imagine (/img)">
                    <ImageIcon className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                    className="text-zinc-400 hover:text-amber-400 transition-colors" 
                    title="Emoticoane"
                  >
                    <Smile className="w-3.5 h-3.5" />
                  </button>
                </div>
                
                <button 
                  type="submit" 
                  disabled={!inputText.trim()} 
                  className="tf-btn-primary tf-clip-btn px-4 py-1.5 text-xs uppercase font-black tracking-wider flex items-center gap-1.5 disabled:opacity-50"
                >
                  <span>Trimite</span>
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="p-3 bg-[#090c12] border-t border-white/10 text-center text-xs text-zinc-500 italic font-mono">
            Trebuie să fii autentificat pentru a scrie în chat.
          </div>
        )}

        {/* ModActionModal */}
        {isModActionModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-md bg-[#141414] border border-white/10 rounded-lg shadow-2xl overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between bg-[#0E0E0E]">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                  Acțiune Moderare: {modActionData.actionType.toUpperCase()}
                </h3>
                <button 
                  onClick={() => setIsModActionModalOpen(false)}
                  className="text-zinc-500 hover:text-white transition-colors text-xl font-bold"
                >
                  &times;
                </button>
              </div>
              
              <form onSubmit={handleModActionSubmit} className="p-5 flex flex-col gap-4 text-left">
                <div>
                  <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Membru Vizat</label>
                  <input 
                    type="text" 
                    value={modActionData.targetUsername}
                    disabled
                    className="w-full bg-[#1A1A1A]/50 border border-white/5 rounded px-3 py-2 text-sm text-zinc-400 outline-none cursor-not-allowed"
                  />
                </div>

                {modActionData.actionType !== "kick" && (
                  <div>
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Durată</label>
                    <select 
                      value={modDuration} 
                      onChange={(e) => setModDuration(parseInt(e.target.value))}
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[var(--tf-primary)]"
                    >
                      <option value="15">15 Minute</option>
                      <option value="60">1 Oră</option>
                      <option value="1440">1 Zi</option>
                      <option value="10080">7 Zile</option>
                      <option value="0">Permanent</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Motiv Sancțiune *</label>
                  <textarea 
                    value={modReason}
                    onChange={(e) => setModReason(e.target.value)}
                    placeholder="Introduceți motivul clar al sancțiunii..."
                    required
                    rows="3"
                    className="w-full bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[var(--tf-primary)] resize-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Încărcare Dovadă (Foto / Video)</label>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input 
                        type="file" 
                        accept="image/*,video/*"
                        onChange={handleProofUpload}
                        disabled={isUploadingProof}
                        className="hidden"
                        id="proof-upload-input"
                      />
                      <label 
                        htmlFor="proof-upload-input"
                        className="cursor-pointer bg-[#1A1A1A] hover:bg-zinc-850 border border-white/10 px-4 py-2 rounded text-xs text-white font-semibold transition-colors flex items-center gap-1.5"
                      >
                        {isUploadingProof ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            Se încarcă...
                          </>
                        ) : (
                          <>Alege fișier</>
                        )}
                      </label>
                      {uploadProofSuccess && (
                        <span className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                          ✓ Încărcat cu succes!
                        </span>
                      )}
                    </div>
                    <input 
                      type="text" 
                      placeholder="Sau introduceți link-ul manual..."
                      value={modProofUrl}
                      onChange={(e) => setModProofUrl(e.target.value)}
                      className="w-full bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-2 border-t border-white/5 pt-4">
                  <button 
                    type="button"
                    onClick={() => setIsModActionModalOpen(false)}
                    className="bg-transparent hover:bg-white/5 px-4 py-2 rounded text-xs text-zinc-400 hover:text-white transition-colors"
                  >
                    Anulează
                  </button>
                  <button 
                    type="submit"
                    disabled={isUploadingProof}
                    className="tf-btn-primary px-5 py-2 rounded text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1 disabled:opacity-50"
                  >
                    Confirmă Sancțiunea
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* AppealModal */}
        {isAppealModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="w-full max-w-lg bg-[#141414] border border-white/10 rounded-lg shadow-2xl overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between bg-[#0E0E0E]">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                  🛡️ Contestație Sancțiune Chatbox
                </h3>
                <button 
                  onClick={handleCloseAppeal}
                  className="text-zinc-500 hover:text-white transition-colors text-xl font-bold"
                >
                  &times;
                </button>
              </div>
              
              <form onSubmit={handleAppealSubmit} className="p-5 flex flex-col gap-4 text-left">
                <div className="bg-[#1A1A1A] border border-white/5 rounded-lg p-4 flex flex-col gap-2.5 text-xs text-zinc-300">
                  <div className="flex justify-between border-b border-white/5 pb-1.5">
                    <span className="font-bold uppercase text-zinc-500">Tip Sancțiune:</span>
                    <span className="font-bold text-red-500 uppercase">{appealData.actionType}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-1.5">
                    <span className="font-bold uppercase text-zinc-500">Sancționat de:</span>
                    <span className="font-semibold text-white">{appealData.moderatorUsername}</span>
                  </div>
                  <div className="flex flex-col gap-1 border-b border-white/5 pb-1.5">
                    <span className="font-bold uppercase text-zinc-500">Motivul invocat:</span>
                    <span className="text-zinc-200 italic">"{appealData.reason}"</span>
                  </div>
                  {appealData.proofUrl && (
                    <div className="flex flex-col gap-1.5">
                      <span className="font-bold uppercase text-zinc-500">Dovadă atașată:</span>
                      <div className="max-h-[150px] overflow-hidden rounded border border-white/10 flex items-center justify-center bg-black/40">
                        {appealData.proofUrl.toLowerCase().endsWith(".mp4") || appealData.proofUrl.toLowerCase().endsWith(".webm") ? (
                          <video src={appealData.proofUrl} controls className="max-h-[150px] w-auto max-w-full" />
                        ) : (
                          <img src={appealData.proofUrl} alt="Dovadă" className="max-h-[150px] w-auto max-w-full object-contain" />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Motivația Contestației dumneavoastră *</label>
                  <textarea 
                    value={appealText}
                    onChange={(e) => setAppealText(e.target.value)}
                    placeholder="Scrieți argumentele dumneavoastră pentru care considerați că sancțiunea a fost aplicată greșit..."
                    required
                    rows="4"
                    className="w-full bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-sm text-white outline-none focus:border-[var(--tf-primary)] resize-none"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 mt-2 border-t border-white/5 pt-4">
                  <button 
                    type="button"
                    onClick={handleCloseAppeal}
                    className="bg-transparent hover:bg-white/5 px-4 py-2 rounded text-xs text-zinc-400 hover:text-white transition-colors"
                  >
                    Închide
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmittingAppeal}
                    className="tf-btn-primary px-5 py-2 rounded text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1 disabled:opacity-50"
                  >
                    {isSubmittingAppeal ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        Se trimite...
                      </>
                    ) : (
                      <>Trimite Contestația</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MessageIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function formatTimestamp(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
